import {useCallback, useEffect, useMemo, useRef} from 'preact/hooks';

import {AuthorizeIframe} from '~/components/AuthorizeIframe';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import {useAdjustedUxMode} from '~/hooks/useAdjustedUxMode';
import {useAuthorizeUrl} from '~/hooks/useAuthorizeUrl';
import {useCompleteLogin} from '~/hooks/useCompleteLogin';
import {useElementEventListener} from '~/hooks/useElementEventListener';
import {useEmailListener} from '~/hooks/useEmailListener';
import {useWindoidMessageHandler} from '~/hooks/useWindoidMessageHandler';
import {useWindoidPreAuthPromptContinue} from '~/hooks/useWindoidPreAuthPromptContinue';
import type {
  AuthorizeLoadedEvent,
  CompletedEvent,
  StartEvent,
} from '~/types/event';
import type {IframeElement} from '~/types/iframe';
import {isoDocument} from '~/utils/document';
import register from '~/utils/register';
import {displayToast} from '~/utils/toast';
import {openWindoid} from '~/utils/windoid';
import {isoWindow} from '~/utils/window';

import {Iframe} from './components/Iframe';
import {getAnalyticsProps} from './monorail';
import type {LeadCaptureProps, LeadCaptureWebComponentProps} from './types';

export function LeadCaptureInternal({
  devMode = false,
  emailInputSelector,
  flow = 'discount',
  onComplete,
  onError,
  onRestart: onRestartProp,
  uxMode = 'iframe',
  ...props
}: LeadCaptureProps) {
  const {trackPageImpression, trackUserAction} = useMonorail();
  const iframeRef = useRef<IframeElement | null>(null);
  const windoidRef = useRef<WindowProxy | null>(null);
  const activatorElementRef = useRef<HTMLElement | null>(
    emailInputSelector ? isoDocument.getElementById(emailInputSelector) : null,
  );

  useEffect(() => {
    trackPageImpression({
      page: 'SDK_HAS_LOADED_INITIAL_PAGE',
    });
  }, [trackPageImpression]);

  const {updateEmailToPost, getSubmittedEmail} = useEmailListener({
    emailInputSelector,
    hideChange: false,
    iframeRef,
  });

  /**
   * Windoid
   */
  const adjustedUxMode = useAdjustedUxMode({uxMode, defaultUxMode: 'iframe'});
  const {getAuthorizeUrl} = useAuthorizeUrl({
    ...props,
    clientId: '',
    analyticsContext: 'loginWithShopSelfServe',
    codeChallenge: '',
    codeChallengeMethod: 'S256',
    // @TODO: Uncomment when we can handle flow and flowVersion for Windoid
    // flow: 'discount',
    // flowVersion: props.phoneCapture ? 'PHONE_CAPTURE' : 'EMAIL_CAPTURE',
    responseType: 'code',
    uxMode: adjustedUxMode,
  });

  // Windoid message handlers
  const windoidCompleteLogin = useCompleteLogin({
    storefrontOrigin: props.storefrontOrigin,
  });
  const handleWindoidCompleteMessageHandler = useCallback(
    async (completedEvent: CompletedEvent) => {
      const {
        customerAccessToken,
        customerAccessTokenExpiresAt,
        email,
        loggedIn,
      } = completedEvent;
      // @TODO: Add translations
      const toast = {
        subText: 'Will automatically apply at checkout',
        text: 'Discount saved',
      };

      displayToast(toast);

      windoidCompleteLogin(completedEvent);

      onComplete({
        customerAccessToken,
        customerAccessTokenExpiresAt,
        email: email || '',
        signedIn: loggedIn,
        // @TODO: Hardcoded values for now until they are returned in the CompletedEvent
        emailVerified: true,
        phoneShareConsent: true,
      });
    },
    [onComplete, windoidCompleteLogin],
  );

  const handleWindoidMessage = useWindoidMessageHandler({
    windoidRef,
    handleComplete: handleWindoidCompleteMessageHandler,
  });

  const openWindoidOrRedirect = useCallback(
    (src: string) => {
      switch (adjustedUxMode) {
        case 'windoid':
          windoidRef.current = openWindoid(src, handleWindoidMessage);
          break;
        case 'redirect':
          isoWindow.location.href = src;
          break;
      }
    },
    [adjustedUxMode, handleWindoidMessage],
  );

  // Pre-auth prompt
  const preAuthPromptIframeSrc = getAuthorizeUrl({
    uxRole: 'prompt',
  });
  const handlePromptContinue = useWindoidPreAuthPromptContinue({
    getAuthorizeUrl,
    getEmail: getSubmittedEmail,
    openWindoid: openWindoidOrRedirect,
    iframeRef,
  });

  const elementEventListeners = useMemo(
    () => ({
      notifyEmailFieldShown: () => {
        trackPageImpression({
          page: 'PARTNER_EMAIL_INPUT_SHOWN',
        });
      },
      start: ({detail}: StartEvent) => {
        trackUserAction({
          userAction: 'START_CALLED',
        });
        const email = detail.email;

        if (devMode) {
          if (email === 'success-not-a-shop-user@myshopify.io') {
            onComplete?.({email, signedIn: false});
            return;
          }

          if (email === 'blocked-user@myshopify.io') {
            onError?.({
              code: 'user_blocked',
              message: 'Hardcoded error event test email was entered.',
              email,
            });
            return;
          }
        }

        if (adjustedUxMode === 'windoid') {
          const authorizeUrl = getAuthorizeUrl({
            loginHint: email,
          });
          // @TODO: Do not open windoid if the email does not correspond to a Shop Pay user
          windoidRef.current = openWindoid(authorizeUrl, handleWindoidMessage);
          return;
        }

        updateEmailToPost(email);
      },
    }),
    [
      adjustedUxMode,
      getAuthorizeUrl,
      devMode,
      handleWindoidMessage,
      onComplete,
      onError,
      trackPageImpression,
      trackUserAction,
      updateEmailToPost,
    ],
  );

  useElementEventListener(elementEventListeners);

  const onLoaded = ({userFound}: AuthorizeLoadedEvent) => {
    props.onLoad?.({userFound});
  };

  const onRestart = useCallback(() => {
    updateEmailToPost('');
    onRestartProp?.();
  }, [onRestartProp, updateEmailToPost]);

  if (flow === 'discount') {
    if (adjustedUxMode === 'windoid') {
      return (
        <AuthorizeIframe
          activator={activatorElementRef}
          autoOpen={false}
          onError={onError}
          onLoaded={onLoaded}
          // @TODO: handle onPromptChange
          onPromptChange={() => {}}
          onPromptContinue={handlePromptContinue}
          proxy={false}
          ref={iframeRef}
          src={preAuthPromptIframeSrc}
          modalType="wide"
          variant="default"
        />
      );
    }

    return (
      <Iframe
        {...props}
        devMode={devMode}
        onComplete={onComplete}
        onError={onError}
        onRestart={onRestart}
        ref={iframeRef}
      />
    );
  }

  return null;
}

register<LeadCaptureProps>(
  ({devMode, element, ...props}) => (
    <RootProvider
      devMode={devMode}
      element={element}
      featureName="ShopLeadCapture"
      monorailProps={getAnalyticsProps(props)}
    >
      <LeadCaptureInternal {...props} devMode={devMode} />
    </RootProvider>
  ),
  {
    methods: ['notifyEmailFieldShown', 'start'],
    name: 'shop-lead-capture',
    props: {
      apiKey: 'string',
      devMode: 'boolean',
      emailInputSelector: 'string',
      flow: 'string',
      onAuthenticate: 'function',
      onComplete: 'function',
      // @TODO: call this event in windoid
      onConfirmSuccess: 'function',
      onError: 'function',
      onLoad: 'function',
      onRestart: 'function',
      phoneCapture: 'boolean',
      phoneCaptureDisclosureText: 'string',
      storefrontOrigin: 'string',
      uxMode: 'string',
    },
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-lead-capture']: LeadCaptureWebComponentProps;
    }
  }
}

export function ShopLeadCapture(props: LeadCaptureWebComponentProps) {
  return <shop-lead-capture {...props} />;
}
